import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmationService } from 'primeng/api';
import { SvgIconComponent } from '../../../shared/svg-icon/svg-icon.component';
import { LinkedFile, FileItem } from '../../../models';
import { UiStateService } from '../../../services/ui-state.service';
import { getFileIcon } from '../../right-panel/file-type.utils';

@Component({
    selector: 'app-document-link-area',
    standalone: true,
    imports: [CommonModule, FormsModule, SvgIconComponent, ConfirmDialogModule, CheckboxModule],
    providers: [ConfirmationService],
    templateUrl: './document-link-area.component.html',
    styleUrl: './document-link-area.component.scss',
})
export class DocumentLinkArea {
    selectedCategoryLabel = input<string | null>(null);
    selectedCategoryParentLabel = input<string | null>(null);
    linkedFiles = input<LinkedFile[]>([]);

    onUnlink = output<LinkedFile>();
    onMassUnlink = output<LinkedFile[]>();
    onFilesDropped = output<FileItem[]>();
    onNavigate = output<{ path: string; fileName: string }>();
    onPreview = output<{ path: string; fileName: string }>();

    isDragOver = signal(false);
    selectedFiles = signal<LinkedFile[]>([]);

    canNavigate = computed(() => {
        const selected = this.selectedFiles();
        return selected.length === 1 && !!selected[0].filePath;
    });

    canPreview = computed(() => {
        const selected = this.selectedFiles();
        return selected.length === 1 && !!selected[0].filePath;
    });

    private uiStateService = inject(UiStateService);

    constructor(private confirmationService: ConfirmationService) {
        effect(() => {
            // Reset selection when linkedFiles change (e.g. category switch)
            this.linkedFiles();
            this.selectedFiles.set([]);
        }, { allowSignalWrites: true });
    }

    getFileIcon(extension: string): string {
        return getFileIcon(extension);
    }

    onDragEnter(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        if (this.uiStateService.isInternalDragging()) {
            this.isDragOver.set(true);
        }
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();

        if (!this.uiStateService.isInternalDragging()) {
            if (event.dataTransfer) {
                event.dataTransfer.dropEffect = 'none';
            }
            return;
        }

        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'copy';
        }
        this.isDragOver.set(true);
    }

    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver.set(false);
    }

    onDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver.set(false);

        if (!this.uiStateService.isInternalDragging()) {
            return;
        }

        const data = event.dataTransfer?.getData('application/json');
        if (data) {
            try {
                const files: FileItem[] = JSON.parse(data);
                if (Array.isArray(files) && files.length > 0) {
                    this.onFilesDropped.emit(files);
                }
            } catch (e) {
                console.error('Error parsing dropped files data:', e);
            }
        }
    }

    navigateSelected(): void {
        if (!this.canNavigate()) return;
        const file = this.selectedFiles()[0];
        this.onNavigate.emit({ path: file.filePath!, fileName: file.name });
    }

    previewSelected(): void {
        if (!this.canPreview()) return;
        const file = this.selectedFiles()[0];
        const dir = file.filePath!;
        const fullPath = dir + (dir.endsWith('/') ? '' : '/') + file.name;
        this.onPreview.emit({ path: fullPath, fileName: file.name });
    }

    confirmUnlink(file: LinkedFile): void {
        //TODO - UX - szöveg véglegesítése
        this.confirmationService.confirm({
            message: `Biztosan le szeretné venni a csatolást a(z) "${file.name}" fájlról? A fájl nem törlődik, csak a logikai kapcsolat szűnik meg.`,
            header: 'Csatolás levétele',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Igen',
            rejectLabel: 'Nem',
            accept: () => {
                this.onUnlink.emit(file);
            }
        });
    }

    confirmMassUnlink(): void {
        const selected = this.selectedFiles();
        if (selected.length === 0) return;

        this.confirmationService.confirm({
            message: `Biztosan le szeretné venni a csatolást a kiválasztott ${selected.length} fájlról? A fájlok nem törlődnek, csak a logikai kapcsolatuk szűnik meg.`,
            header: 'Csatolások levétele',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Igen',
            rejectLabel: 'Nem',
            accept: () => {
                this.onMassUnlink.emit(selected);
                this.selectedFiles.set([]);
            }
        });
    }
}
